import { addToCart as addLines } from '@lucid/card';
import { StandardEvents } from '@shopify/events';

/**
 * Cart adds go through the one shared implementation in @lucid/card; this wrapper keeps the { ok, message } result
 * the cart components expect.
 *
 * @param {Array<{ id: number | string, quantity?: number, properties?: Record<string, string> }>} lines
 * @param {{ target?: EventTarget, source?: string }} [options]
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function addToCart(lines, { target = document, source = 'lucid-cart' } = {}) {
  const items = lines.filter((line) => line && line.id);
  if (!items.length) return { ok: false };
  try {
    await addLines(items, { source: target, origin: source });
    return { ok: true };
  } catch (error) {
    return { ok: false, message: error?.handled ? error.message : '' };
  }
}

/**
 * Free shipping progress. Rendered on the server in the cart currency; when the cart is shown in another currency
 * than the shop currency the threshold is converted with the storefront rate and the copy is rebuilt here.
 */
class LucidCartProgress extends HTMLElement {
  static observedAttributes = ['data-total'];

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#update();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  attributeChangedCallback() {
    this.#update();
  }

  /** Amount still missing for free shipping, in the cart currency's minor unit. */
  get remaining() {
    const rate = this.dataset.convert === 'true' ? Number(window.Shopify?.currency?.rate || 1) : 1;
    const threshold = Math.round(Number(this.dataset.threshold || 0) * rate);
    if (!threshold) return 0;
    return Math.max(threshold - Number(this.dataset.total || 0), 0);
  }

  #update() {
    if (this.dataset.convert !== 'true') return;
    // The cart morph puts the server copy back (empty text, unconverted bar), so converted values are reapplied.
    if (!this.#observer) this.#observer = new MutationObserver(() => this.#update());
    this.#observer.disconnect();
    this.#apply();
    this.#observer.takeRecords();
    if (this.isConnected) this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  }

  #apply() {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    const threshold = Math.round(Number(this.dataset.threshold || 0) * rate);
    const total = Number(this.dataset.total || 0);
    if (!threshold) return;

    const remaining = Math.max(threshold - total, 0);
    const percent = Math.min(100, Math.round((total / threshold) * 100));
    const text = this.querySelector('[data-progress-text]');
    const bar = this.querySelector('[data-progress-bar]');
    const meter = this.querySelector('[role="progressbar"]');

    if (text) {
      if (remaining === 0) {
        text.textContent = this.dataset.reached || '';
      } else {
        const amount = this.#format(remaining);
        text.textContent = (this.dataset.remaining || '').replace('[amount]', amount);
      }
    }
    if (bar instanceof HTMLElement) bar.style.setProperty('--lc-progress', String(percent / 100));
    meter?.setAttribute('aria-valuenow', String(percent));
    this.classList.toggle('is-complete', remaining === 0);
  }

  /** @param {number} cents */
  #format(cents) {
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || undefined;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }
}

if (!customElements.get('lucid-cart-progress')) {
  customElements.define('lucid-cart-progress', LucidCartProgress);
}

/** In-memory cache of rendered rails, keyed by URL. */
const railCache = new Map();

/**
 * Product rail loaded through the Section Rendering API (complementary recommendations or a collection).
 * The cart section morph resets the slot to its server state, so the element re-injects its cached markup
 * whenever that happens and refetches only when its URL changes.
 */
class LucidCartRail extends HTMLElement {
  static observedAttributes = ['data-url', 'data-in-cart', 'data-in-cart-types', 'data-cart-total'];

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {IntersectionObserver | null} */
  #visibility = null;

  #requested = '';

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#observer = new MutationObserver(() => this.#restore());
    this.#observer.observe(this, { childList: true, subtree: true });

    if (this.dataset.loading === 'eager') {
      this.#load();
      return;
    }

    this.#visibility = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#visibility?.disconnect();
        this.#visibility = null;
        this.#load();
      },
      { rootMargin: '200px 0px' }
    );
    this.#visibility.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    this.#visibility?.disconnect();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('[data-upsell-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      this.#toggleSizes(toggle);
      return;
    }
    const add = target?.closest('[data-upsell-add]');
    if (add instanceof HTMLButtonElement) this.#add(add);
  };

  /** @param {HTMLButtonElement} toggle */
  #toggleSizes(toggle) {
    const sizes = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!sizes) return;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    for (const other of this.querySelectorAll('[data-upsell-toggle][aria-expanded="true"]')) {
      if (other === toggle) continue;
      other.setAttribute('aria-expanded', 'false');
      const otherSizes = document.getElementById(other.getAttribute('aria-controls') || '');
      if (otherSizes) otherSizes.hidden = true;
    }
    toggle.setAttribute('aria-expanded', String(open));
    sizes.hidden = !open;
    if (open) sizes.querySelector('button')?.focus();
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const id = button.dataset.upsellAdd;
    const row = button.closest('[data-upsell-row]');
    if (!id || !row || row.hasAttribute('aria-busy')) return;
    const status = row.querySelector('[data-upsell-status]');
    row.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';

    const result = await addToCart([{ id, quantity: 1 }], { target: this, source: 'lucid-cart-upsell' });
    if (result.ok) announce(this.dataset.addedText, row.dataset.title);
    if (!row.isConnected) return;
    row.removeAttribute('aria-busy');
    button.removeAttribute('aria-busy');
    if (!result.ok && status) status.textContent = result.message || this.dataset.errorText || '';
  }

  /**
   * Drops rows for products that are in the cart by now (cached markup), of a product type the cart already holds,
   * shown by the set hint or of a type an earlier row already shows, orders the rest and caps the list.
   */
  #prune() {
    const slot = this.#slot;
    if (!(slot instanceof HTMLElement)) return 0;
    const rows = Array.from(slot.querySelectorAll(':scope > [data-rail-row]')).filter((row) => row instanceof HTMLElement);
    if (!rows.length) return slot.children.length;
    const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
    const inCartTypes = new Set((this.dataset.inCartTypes || '').split('|').filter(Boolean));
    const setHint = this.#scope.querySelector('.lucid-cart-set[data-product-id]');
    const setProduct = setHint?.getAttribute('data-product-id') || '';
    const setType = (setHint?.getAttribute('data-type') || '').toLowerCase();
    if (setType) inCartTypes.add(setType);
    const shownTypes = new Set();
    const kept = rows.filter((row) => {
      const id = row.dataset.productId || '';
      const type = (row.dataset.type || '').toLowerCase();
      const parts = (row.dataset.parts || '').toLowerCase();
      const drop =
        inCart.has(id) ||
        (setProduct !== '' && id === setProduct) ||
        (type !== '' && inCartTypes.has(type)) ||
        (type !== '' && shownTypes.has(type)) ||
        Array.from(inCartTypes).some((cartType) => parts.includes(cartType));
      if (drop) row.remove();
      else if (type !== '') shownTypes.add(type);
      return !drop;
    });
    const ordered = this.dataset.order === 'gap' ? this.#order(kept) : kept;
    const limit = Number(this.dataset.limit || ordered.length);
    ordered.forEach((row, index) => {
      if (index < limit) slot.append(row);
      else row.remove();
    });
    return Math.min(limit, ordered.length);
  }

  /**
   * Pieces that close the free shipping gap come first (smallest price first), jeans go last unless they close it.
   * @param {HTMLElement[]} rows
   */
  #order(rows) {
    const progress = this.#scope.querySelector('lucid-cart-progress');
    const gap = progress && 'remaining' in progress ? Number(progress.remaining) || 0 : 0;
    const closing = [];
    const rest = [];
    const jeans = [];
    for (const row of rows) {
      const price = Number(row.dataset.price || 0);
      if (gap > 0 && price >= gap) closing.push(row);
      else if (row.dataset.jeans === 'true') jeans.push(row);
      else rest.push(row);
    }
    closing.sort((a, b) => Number(a.dataset.price || 0) - Number(b.dataset.price || 0));
    return [...closing, ...rest, ...jeans];
  }

  get #scope() {
    return this.closest('cart-items-component') || document;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected || this.#visibility) return;
    if (name !== 'data-url') {
      const slot = this.#slot;
      if (slot instanceof HTMLElement) delete slot.dataset.renderedUrl;
      this.#restore();
      return;
    }
    this.#load();
  }

  get #slot() {
    return this.querySelector('[data-rail-slot]');
  }

  get #wrapper() {
    return this.querySelector('[data-rail-wrapper]');
  }

  #restore() {
    const slot = this.#slot;
    const url = this.dataset.url || '';
    const cached = railCache.get(url);
    if (!slot || cached === undefined || slot.dataset.renderedUrl === url) return;
    this.#render(url, cached);
  }

  async #load() {
    const url = this.dataset.url || '';
    if (!url || this.#requested === url) return this.#restore();
    this.#requested = url;

    let markup = railCache.get(url);
    if (markup === undefined) {
      markup = await this.#fetchProducts(url);
      const fallback = this.dataset.fallbackUrl;
      // Complementary results are often few, and the cart filters drop more, so related products top them up.
      // A curated list only gets topped up when it is shorter than the rail.
      if (fallback && this.#rowCount(markup) < Number(this.dataset.fill || 10)) {
        markup = this.#merge(markup, await this.#fetchProducts(fallback));
      }
      railCache.set(url, markup);
    }
    if ((this.dataset.url || '') !== url) return;
    this.#render(url, markup);
  }

  /** @param {string} markup */
  #rowCount(markup) {
    if (!markup) return 0;
    const template = document.createElement('template');
    template.innerHTML = markup;
    return template.content.children.length;
  }

  /**
   * Appends the rows of the second list whose product is not in the first one yet.
   * @param {string} first
   * @param {string} second
   */
  #merge(first, second) {
    if (!second) return first;
    if (!first) return second;
    const template = document.createElement('template');
    template.innerHTML = first;
    const ids = new Set(Array.from(template.content.children, (row) => row.getAttribute('data-product-id')));
    const extra = document.createElement('template');
    extra.innerHTML = second;
    for (const row of Array.from(extra.content.children)) {
      const id = row.getAttribute('data-product-id');
      if (id && ids.has(id)) continue;
      template.content.append(row);
    }
    return template.innerHTML;
  }

  /** @param {string} url */
  async #fetchProducts(url) {
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return '';
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('[data-rail-products]');
      return list && list.children.length ? list.innerHTML : '';
    } catch {
      return '';
    }
  }

  /**
   * @param {string} url
   * @param {string} markup
   */
  #render(url, markup) {
    const slot = this.#slot;
    const wrapper = this.#wrapper;
    if (!(slot instanceof HTMLElement) || !(wrapper instanceof HTMLElement)) return;

    this.#observer?.disconnect();
    slot.innerHTML = markup;
    slot.dataset.renderedUrl = url;
    wrapper.hidden = !markup || this.#prune() === 0;
    this.#observer?.observe(this, { childList: true, subtree: true });
  }
}

if (!customElements.get('lucid-cart-rail')) {
  customElements.define('lucid-cart-rail', LucidCartRail);
}

/** Inline add form used by the set hint in the cart: variant select plus one add button. */
class LucidCartAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const option = select.selectedOptions[0];
    const price = this.querySelector('[data-add-price]');
    if (price && option?.dataset.price) price.textContent = option.dataset.price;
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const button = form.querySelector('[type="submit"]');
    const status = this.querySelector('[data-add-status]');
    const id = new FormData(form).get('id');
    if (!id) return;

    button?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    const result = await addToCart([{ id: String(id), quantity: 1 }], { target: this, source: 'lucid-cart-set' });
    button?.removeAttribute('aria-busy');
    if (result.ok) announce(this.dataset.addedText, this.dataset.title);
    if (!result.ok && status) status.textContent = result.message || this.dataset.errorText || '';
  };
}

if (!customElements.get('lucid-cart-add')) {
  customElements.define('lucid-cart-add', LucidCartAdd);
}

/**
 * Polite live region for adds that happen inside the cart (upsell rows, set hint): the row itself is gone after the
 * cart re-renders, so the confirmation is spoken from a region outside of it.
 * @param {string | undefined} template - Text with a [title] placeholder
 * @param {string | undefined} title
 */
function announce(template, title) {
  if (!template) return;
  let region = document.querySelector('[data-lucid-cart-live]');
  if (!(region instanceof HTMLElement)) {
    region = document.createElement('p');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.dataset.lucidCartLive = '';
    document.body.append(region);
  }
  const live = region;
  live.textContent = '';
  window.setTimeout(() => {
    live.textContent = template.replace('[title]', title || '');
  }, 120);
}

/** One small scale bounce of every cart count bubble. */
function bumpCartBubble() {
  if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
  for (const bubble of document.querySelectorAll('cart-icon .cart-bubble')) {
    if (!(bubble instanceof HTMLElement)) continue;
    bubble.classList.remove('lucid-cart-bump');
    void bubble.offsetWidth;
    bubble.classList.add('lucid-cart-bump');
  }
}

/** @param {AnimationEvent} event */
function onBumpEnd(event) {
  if (event.animationName !== 'lucid-cart-bump' || !(event.target instanceof Element)) return;
  event.target.classList.remove('lucid-cart-bump');
}

/**
 * Confirmation for every successful add, wherever it comes from: bubble bounce, and a short haptic tick on touch
 * devices that support it. Errors keep everything still.
 * @param {Event & { action?: string, promise?: Promise<{ detail?: { didError?: boolean } }> }} event
 */
function onLinesUpdate(event) {
  if (event.action !== 'add' || !event.promise) return;
  event.promise
    .then((result) => {
      if (result?.detail?.didError) return;
      bumpCartBubble();
      if (window.matchMedia('(pointer: coarse)').matches) navigator.vibrate?.(12);
    })
    .catch(() => {});
}

document.addEventListener(StandardEvents.cartLinesUpdate, onLinesUpdate);
document.addEventListener('animationend', onBumpEnd);
